import { Graphic, GraphicMeta } from '@models/Survey/Survey'

const isPresentNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value)

/**
 * Slot where the user's artwork (flag / poster / cover) replaces a default layer.
 * Prefer the topmost defaultGraphic — birthplace ships a z=99 backing plate before the z=100 flag slot.
 *
 * When z-index ties, prefer a layer **without** explicit width/height (e.g. drapeau-trim): the
 * placeholder often carries an oversized meta box; size comes from `defaultCountryImageBox`
 * (trim hole + slight underlap under FrameDrapeaux).
 */
export const pickAnswerSlotGraphic = (defaults: Graphic[] | undefined): Graphic | undefined => {
  if (!defaults?.length) {
    return undefined
  }
  return [...defaults]
    .sort((a, b) => {
      const byZ = (a.zIndex ?? 0) - (b.zIndex ?? 0)
      if (byZ !== 0) {
        return byZ
      }
      const aSized = isPresentNumber(a.meta?.width) && isPresentNumber(a.meta?.height) ? 1 : 0
      const bSized = isPresentNumber(b.meta?.width) && isPresentNumber(b.meta?.height) ? 1 : 0
      // Unsized last → chosen by at(-1) among the same z (induced box from fallback).
      return bSized - aSized
    })
    .at(-1)
}

/**
 * Merge slot meta with hardcoded fallbacks.
 * Null width/height → use fallback (induced frame-hole box), never the answer bitmap’s natural size.
 */
export const resolveAnswerGraphicMeta = (slot: Graphic | undefined, fallback: GraphicMeta): GraphicMeta => {
  const m = slot?.meta
  return {
    ...fallback,
    xOffset: isPresentNumber(m?.xOffset) ? m.xOffset : fallback.xOffset,
    yOffset: isPresentNumber(m?.yOffset) ? m.yOffset : fallback.yOffset,
    // Only trust explicit sizes from the slot when both are set; otherwise keep the frame-hole box.
    width: isPresentNumber(m?.width) && isPresentNumber(m?.height) ? m.width : fallback.width,
    height: isPresentNumber(m?.width) && isPresentNumber(m?.height) ? m.height : fallback.height,
    preserveAspectRatio: m?.preserveAspectRatio ?? fallback.preserveAspectRatio,
    zIndex: slot?.zIndex ?? fallback.zIndex,
  }
}

/**
 * Artwork inside a frame (flag / movie poster / album cover).
 * Always use the induced box — trim defaults often ship `x/y=0` and null size, which would
 * place the answer bitmap at the canvas origin. Keep content zIndex from the box so static
 * frame layers (often z=110) paint on top.
 */
export const resolveFramedArtworkMeta = (_slot: Graphic | undefined, frameHole: GraphicMeta): GraphicMeta => ({
  ...frameHole,
  width: frameHole.width,
  height: frameHole.height,
  xOffset: frameHole.xOffset,
  yOffset: frameHole.yOffset,
  preserveAspectRatio: frameHole.preserveAspectRatio ?? 'none',
  zIndex: frameHole.zIndex,
})

/** @deprecated alias — use resolveFramedArtworkMeta */
export const resolveCountryFlagMeta = resolveFramedArtworkMeta
